class DeferredSystem<S> {
  private instance: S | undefined

  private state: 'not_ready' | 'starting' | 'ready' = 'not_ready'

  private queuedListeners: ((instance: S) => void)[] = []

  private initializationFunction:
    | (() => S | undefined | Promise<S | undefined>)
    | undefined

  private autoInitialize = false

  constructor(
    initializationFunction:
      | (() => S | undefined | Promise<S | undefined>)
      | undefined = undefined,
    autoInitialize = !!initializationFunction
  ) {
    this.initializationFunction = initializationFunction
    this.autoInitialize = !!initializationFunction && autoInitialize
  }

  private resolveQueue = () => {
    if (this.state === 'ready' && this.instance !== undefined) {
      while (this.queuedListeners.length > 0) {
        const listener = this.queuedListeners.shift()
        if (listener) {
          listener(this.instance)
        }
      }
    }
  }

  public init = async (instance: S | undefined = undefined) => {
    if (instance) {
      this.instance = instance
    } else {
      if (this.state !== 'not_ready' || !this.initializationFunction) return
      this.state = 'starting'
      this.instance = await this.initializationFunction()
    }

    if (this.instance !== undefined) {
      this.state = 'ready'
      this.resolveQueue()
    } else {
      this.state = 'not_ready'
    }
  }

  public runWhenReady = (subscriber: (system: S) => void) => {
    if (this.queuedListeners.includes(subscriber)) return

    if (this.instance) {
      subscriber(this.instance)
    } else {
      this.queuedListeners.push(subscriber)
      if (this.autoInitialize) {
        this.init()
      }
    }
  }

  public get = () => new Promise<S>(this.runWhenReady)

  public getSync = () => this.instance
}

export { DeferredSystem }
